import { NewCheckoutI } from "../../../../../api/request/Lead/Interfaces/DataInterface.response";
import { PromotionV3T } from "./types";
import { numberFormatPayment } from "../../../../../utils/Utils";

/**
 * Whole-euro promo amounts render without decimals ("1 €", not "1,00 €").
 */
export const formatPromoPrice = (value: number): string =>
  Number.isInteger(value) ? String(value) : numberFormatPayment(value);

/**
 * Discount percentage of a promoted price vs its regular price, rounded to the
 * nearest integer. Returns 0 when there is no real discount.
 */
export const getDiscountPercent = (full: number, now: number): number => {
  if (!full || now >= full) return 0;
  return Math.round(((full - now) / full) * 100);
};

export interface IntroMonthsPromoT {
  /** Number of intro months, when we can derive it from the promo code. */
  months?: number;
  /** Price charged during the intro months (e.g. 1 €). */
  introPrice: number;
  /** Regular price charged afterwards, per month. */
  afterPrice: number;
}

/**
 * Only promo codes containing "MESES" (ES "3MESES1E" family) are intro-months
 * promos. A permanent percentage code such as "10_sconto" (10% all year, IT)
 * must never be read as "10 intro months".
 */
export const isIntroMonthsCode = (code?: string): boolean =>
  !!code && /MESES/i.test(code);

/**
 * Extracts the leading integer from an intro-months promo code such as
 * "3MESES1E" → 3. Returns undefined when no leading number is present.
 */
const monthsFromCode = (code?: string): number | undefined => {
  if (!code) return undefined;
  const match = code.match(/^(\d+)/);
  if (!match) return undefined;
  const n = parseInt(match[1], 10);
  return Number.isFinite(n) && n > 0 && n <= 24 ? n : undefined;
};

/**
 * Deduces, for the MONTHLY frequency, whether the active promo is an
 * "intro months" promo (first N months at a reduced price, regular price
 * afterwards) rather than a permanent discount. This is the ES "3MESES1E"
 * pattern: the promoted monthly price (~1 €) only applies to the first months.
 *
 * We only treat it as an intro promo when the code is an intro-months code
 * and the promoted price is genuinely lower than the regular one. Any other
 * discount (e.g. IT "10_sconto", 10% all year) is a permanent one and gets the
 * regular price + discount rendering. When we can't read the number of months
 * from the code, callers fall back to a generic honest copy.
 */
export const getIntroMonthsPromo = (
  checkout: NewCheckoutI,
  promotion: PromotionV3T | undefined,
  monthlyFull: number,
  monthlyNow: number
): IntroMonthsPromoT | undefined => {
  if (!(monthlyNow < monthlyFull)) return undefined;

  const code =
    checkout.promotionalCode || promotion?.promotionalCode || undefined;
  if (!isIntroMonthsCode(code)) return undefined;
  const months = monthsFromCode(code);

  return {
    months,
    introPrice: monthlyNow,
    afterPrice: monthlyFull,
  };
};
